// 🎤 음성 입력 — 폰 네이티브 음성인식 브릿지 (2026-09-06 원격 "B도 가능한가? 체감해봤으면 함. 관리자만")
//   안드로이드 WebView·데스크톱 WebView2 는 Web Speech API 를 지원하지 않으므로 셸의 SpeechRecognizer 를 브릿지로 쓴다.
//   셸(MainActivity.NotifyBridge): speechSupported() / speechStart(reqId, bcp47) / speechStop(reqId)
//   결과는 window 이벤트 'tpt:speech' {reqId, partial?|final?|error?, end?} 로 온다 (원격 서명 브릿지와 같은 패턴).
//   🔴 iOS 셸도 window.TPTAndroid 를 주입하므로 **메서드 존재**로 판별한다 — iOS 가 같은 이름을 구현하면 그대로 동작.
import type { LangCode } from './translate';
import { isAndroidApp, isIosApp } from '../utils/platform';
import { isTauri } from '../desktop/env';

type SpeechBridge = {
	speechSupported?: () => boolean;
	speechStart?: (reqId: string, lang: string) => void;
	speechStop?: (reqId: string) => void;
};
type SpeechEvent = { reqId: string; partial?: string; final?: string; error?: string; end?: boolean };

function bridge(): SpeechBridge | null {
	const b = (window as unknown as { TPTAndroid?: SpeechBridge }).TPTAndroid;
	return b && typeof b.speechStart === 'function' && typeof b.speechStop === 'function' ? b : null;
}

/** 셸이 음성인식을 제공하는가 — 버튼 노출 판정. 브릿지 호출은 반드시 `.call(bridge, …)` (언바인드 호출 함정, GUIDE). */
export function speechAvailable(): boolean {
	const b = bridge();
	if (!b) return false;
	try { return typeof b.speechSupported === 'function' ? !!b.speechSupported.call(b) : true; } catch { return false; }
}

/** 🗣 대면 통역(pages/InterpretPage) 노출 판정 — 안드 앱(폰 음성인식) + 데탑·iOS 앱(녹음 → 서버 받아쓰기, 2026-10-27).
 *  일반 웹 브라우저는 종전처럼 제외. 페이지 파일이 아니라 여기 두는 이유: 메뉴가 이걸 부르는데, 페이지를 import 하면 지연 로딩 청크가 메인 번들로 딸려 온다. */
export function interpretAvailable(): boolean {
	if (isAndroidApp() && !isIosApp()) return speechAvailable() || recorderSpeechAvailable();
	return (isIosApp() || isTauri()) && recorderSpeechAvailable();
}

/** 표시 언어 → 인식기 BCP-47. 사용자의 화면 언어로 말한다고 본다. */
export function speechLocale(lang: LangCode): string {
	return ({ ko: 'ko-KR', en: 'en-US', vi: 'vi-VN', zh: 'zh-CN', es: 'es-MX' } as Record<LangCode, string>)[lang] || 'ko-KR';
}

export type SpeechSession = { stop: () => void };

/**
 * 인식 1회 세션. partial 은 말하는 동안 갱신되는 잠정 문장(누적 아님 — 매번 전체를 다시 준다), final 은 확정 문장.
 * end 는 세션 종료(정상·오류 공통) — 호출측은 여기서 버튼을 idle 로 되돌린다.
 * 🎤 연속 청취(안드 code 91, 2026-09-10): 셸이 문장(세그먼트)마다 final 을 보내고(end 없음) 다시 듣다가 무음 10초에 end 를 보낸다 —
 *   final 은 여러 번 올 수 있으니 호출측은 이어붙인다(ComposerFooter 가 그렇게 한다). 안전 타임아웃은 5분(종전 60초는 연속 모드에서 긴 말을 끊는다).
 */
export function startSpeech(lang: LangCode, on: { partial?: (s: string) => void; final?: (s: string) => void; error?: (code: string) => void; end?: () => void }): SpeechSession | null {
	const b = bridge();
	if (!b || !b.speechStart || !b.speechStop) return null;
	const startFn = b.speechStart; const stopFn = b.speechStop;
	const reqId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
	let done = false;
	const finish = () => {
		if (done) return;
		done = true;
		clearTimeout(timer);
		window.removeEventListener('tpt:speech', onEv as EventListener);
		on.end?.();
	};
	const onEv = (e: CustomEvent<SpeechEvent>) => {
		const d = e.detail;
		if (!d || d.reqId !== reqId) return;
		if (typeof d.partial === 'string') on.partial?.(d.partial);
		if (typeof d.final === 'string') on.final?.(d.final);
		if (d.error) on.error?.(d.error);
		if (d.end || d.error) finish();
	};
	const timer = setTimeout(() => { try { stopFn.call(b, reqId); } catch { /* 무해 */ } finish(); }, 300_000);
	window.addEventListener('tpt:speech', onEv as EventListener);
	try { startFn.call(b, reqId, speechLocale(lang)); } catch { on.error?.('bridge'); finish(); return null; }
	return { stop: () => { try { stopFn.call(b, reqId); } catch { finish(); } } };
}

// ── 🗣 데탑·iOS 대면 통역 — 녹음 → 서버 받아쓰기 (2026-10-27 형 "데탑·iOS 에도 대면 통역") ──────────────────
//   안드 앱처럼 폰 음성인식 브릿지가 없는 데탑(WebView2)·iOS(WKWebView)는 마이크로 한 문장을 녹음해 /api/tpt/stt 로 보낸다.
//   말이 시작된 뒤 1.2초 조용하면 자동으로 끝(최대 30초), 다시 누르면 바로 끝. 결과는 startSpeech 와 같은 콜백(final 1번 → end).
const REC_SILENCE_MS = 1200;
const REC_MAX_MS = 30_000;
function recMime(): string {
	const c = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac', 'audio/ogg;codecs=opus'];
	for (const m of c) { try { if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m; } catch { /* */ } }
	return '';
}
/** 녹음 방식 통역이 가능한가 — 마이크 녹음(MediaRecorder) 지원 */
export function recorderSpeechAvailable(): boolean {
	try { return typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && !!recMime(); } catch { return false; }
}
function recorderSpeech(lang: LangCode, on: { partial?: (s: string) => void; final?: (s: string) => void; error?: (code: string) => void; end?: () => void }): SpeechSession {
	let stopped = false; let done = false;
	let rec: MediaRecorder | null = null; let stream: MediaStream | null = null; let ctx: AudioContext | null = null;
	const timers: number[] = [];
	const finish = () => { if (done) return; done = true; timers.forEach((x) => clearTimeout(x)); try { stream?.getTracks().forEach((t) => t.stop()); } catch { /* */ } try { void ctx?.close(); } catch { /* */ } on.end?.(); };
	const stopRec = () => { if (stopped) return; stopped = true; try { if (rec && rec.state !== 'inactive') rec.stop(); else finish(); } catch { finish(); } };
	void (async () => {
		try {
			stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
			if (stopped) { finish(); return; }
			const mime = recMime();
			rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
			const parts: Blob[] = [];
			rec.ondataavailable = (e) => { if (e.data && e.data.size) parts.push(e.data); };
			rec.onstop = async () => {
				try {
					const blob = new Blob(parts, { type: (rec?.mimeType || mime || 'audio/webm').split(';')[0] });
					if (blob.size < 1500) { on.error?.('no-match'); finish(); return; }
					on.partial?.('⏳');
					const res = await fetch(`/api/tpt/stt?lang=${encodeURIComponent(lang)}`, { method: 'POST', headers: { 'content-type': blob.type }, body: blob });
					const j = await res.json().catch(() => ({})) as { text?: string };
					if (res.ok && j.text) on.final?.(j.text); else on.error?.(res.ok ? 'no-match' : `http-${res.status}`);
				} catch { on.error?.('network'); }
				finish();
			};
			rec.start(250);
			// 🔇 무음 감지 — 말이 한 번 시작된 뒤 REC_SILENCE_MS 조용하면 끝
			try {
				const AC = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
				if (AC) {
					ctx = new AC();
					const an = ctx.createAnalyser(); an.fftSize = 1024;
					ctx.createMediaStreamSource(stream).connect(an);
					const data = new Uint8Array(an.fftSize);
					let spoke = false; let quietSince = 0;
					const tick = () => {
						if (stopped) return;
						an.getByteTimeDomainData(data);
						let sum = 0; for (const v of data) { const d = (v - 128) / 128; sum += d * d; }
						const rms = Math.sqrt(sum / data.length);
						const now = Date.now();
						if (rms > 0.04) { spoke = true; quietSince = 0; } else if (spoke) { if (!quietSince) quietSince = now; else if (now - quietSince > REC_SILENCE_MS) { stopRec(); return; } }
						timers.push(window.setTimeout(tick, 100));
					};
					tick();
				}
			} catch { /* 무음 감지 실패 — 손으로 끝내거나 최대 시간 */ }
			timers.push(window.setTimeout(stopRec, REC_MAX_MS));
		} catch (e) {
			on.error?.(/NotAllowed|Permission/i.test(String((e as Error)?.name || e)) ? 'mic-denied' : 'mic');
			finish();
		}
	})();
	return { stop: stopRec };
}
/** 🗣 대면 통역용 — 폰 음성인식 브릿지가 있으면 그것(안드), 없으면 녹음 → 서버 받아쓰기(데탑·iOS) */
export function startInterpretSpeech(lang: LangCode, on: { partial?: (s: string) => void; final?: (s: string) => void; error?: (code: string) => void; end?: () => void }): SpeechSession | null {
	if (speechAvailable()) return startSpeech(lang, on);
	return recorderSpeechAvailable() ? recorderSpeech(lang, on) : null;
}
